import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Observer } from "gsap/Observer";
import { SplitText } from "gsap/SplitText";

// Registrar plugins de GSAP
gsap.registerPlugin(ScrollTrigger, Observer, SplitText);

function gallerySteps() {
    
    // Buscar todos los contenedores de gallery-steps en la página
    const containers = document.querySelectorAll('[data-gallery-steps]');
    
    // Si no hay contenedores, salir de la función
    if (!containers.length) return;

    // Iterar cada contenedor (puede haber varios gallery-steps en una página)
    containers.forEach(container => {

        // Obtener todas las cards dentro de este contenedor
        const cards = container.querySelectorAll('.gallery-steps__card');
        
        // Si no hay cards, salir de esta iteración
        if (!cards.length) return;

        // ========================================
        // FASE 1: PREPARACIÓN Y CÁLCULO DE DATOS
        // ========================================
        
        // Contador total de secciones (cada card tiene: 1 título + N steps)
        let totalSections = 0;
        
        // Array que almacenará toda la información de cada card
        const cardData = [];

        // Iterar cada card para recopilar información
        cards.forEach((card, cardIndex) => {
            // Encontrar elementos dentro de la card
            const title = card.querySelector('[data-step="title"]');
            const steps = card.querySelectorAll('.gallery-steps__step');
            const navItems = card.querySelectorAll('.gallery-steps__nav-item');
            
            // Establecer estado inicial para el contenido de cada step
            // (oculto y desplazado hacia abajo)
            steps.forEach(step => {
                const content = step.querySelector('.gallery-steps__step-content');
                if (content) {
                    gsap.set(content, { opacity: 0, y: 30 });
                }
            });
            
            // Crear array de secciones: primero el título, luego todos los steps
            const sections = [title, ...steps];
            
            // Guardar información de esta card en cardData
            cardData.push({
                card,                              // Referencia al elemento DOM de la card
                sections,                          // Array con título + steps
                navItems,                          // Elementos de navegación
                titleSplit: null,                  // Se llenará después con SplitText
                startSection: totalSections,       // Índice donde empieza esta card
                endSection: totalSections + sections.length - 1  // Índice donde termina
            });
            
            // Sumar las secciones de esta card al contador total
            totalSections += sections.length;
        });

        // ========================================
        // FASE 2: VARIABLES DE CONTROL
        // ========================================
        
        // Índice de la sección actual (comienza en 0)
        let currentSectionIndex = 0;
        
        // Flag para evitar múltiples animaciones simultáneas
        let isAnimating = false;
        
        // Variable para el Observer (se inicializa después)
        let observer;

        // ========================================
        // FASE 3: SCROLL TRIGGER (PIN)
        // ========================================
        
        // Crear ScrollTrigger para "pinear" el contenedor
        const pinTrigger = ScrollTrigger.create({
            trigger: container,                    // Elemento a observar
            start: 'top top',                      // Cuando el top del container toca el top del viewport
            end: () => `+=${(totalSections - 3) * 100}vh`,  // Espacio de scroll equivalente a las secciones
            pin: true,                             // Fijar el elemento mientras se hace scroll
            pinSpacing: true,                      // Mantener espacio para el scroll
            anticipatePin: 1,                      // Optimización de rendimiento
            
            // Callbacks del ScrollTrigger:
            
            // Cuando entramos al módulo scrolleando hacia abajo
            onEnter: () => {
                currentSectionIndex = 0;           // Resetear a primera sección
                updateActiveStates(0);             // Mostrar primera sección
                observer.enable();                 // Activar el Observer
            },
            
            // Cuando salimos del módulo scrolleando hacia abajo
            onLeave: () => {
                observer.disable();                // Desactivar Observer para permitir scroll normal
            },
            
            // Cuando volvemos al módulo scrolleando hacia arriba
            onEnterBack: () => {
                currentSectionIndex = totalSections - 1;  // Ir a última sección
                updateActiveStates(totalSections - 1);    // Mostrar última sección
                observer.enable();                         // Activar Observer
            },
            
            // Cuando salimos del módulo scrolleando hacia arriba
            onLeaveBack: () => {
                observer.disable();                // Desactivar Observer
                currentSectionIndex = 0;           // Resetear índice
            }
        });

        // ========================================
        // FASE 4: FUNCIÓN DE NAVEGACIÓN
        // ========================================
        
        // Función para ir a una sección específica
        function goToSection(index) {
            // Validaciones: no animar si ya está animando, o si el índice es inválido
            if (isAnimating || index < 0 || index >= totalSections) return;
            
            isAnimating = true;                    // Bloquear nuevas animaciones
            currentSectionIndex = index;           // Actualizar índice actual
            
            updateActiveStates(index);             // Actualizar visualización
            
            // Desbloquear después de 400ms (duración de la animación)
            setTimeout(() => {
                isAnimating = false;
            }, 400);
        }

        // ========================================
        // FASE 5: ACTUALIZACIÓN DE ESTADOS VISUALES
        // ========================================
        
        // Función que actualiza qué elementos están visibles/activos
        function updateActiveStates(sectionIndex) {
            // Iterar cada card para determinar si debe estar visible
            cardData.forEach((data) => {
                // La card está activa si sectionIndex está entre su startSection y endSection
                const isActive = sectionIndex >= data.startSection && sectionIndex <= data.endSection;
                
                if (isActive) {
                    // CARD ACTIVA: mostrar con fade in
                    gsap.to(data.card, { opacity: 1, duration: 0.3 });
                    
                    // Calcular el índice local dentro de esta card (0 = título, 1+ = steps)
                    const localSection = sectionIndex - data.startSection;
                    
                    // Actualizar cada sección (título o step) de esta card
                    data.sections.forEach((section, idx) => {
                        if (idx === localSection) {
                            // SECCIÓN ACTIVA
                            section.classList.add('active');
                            
                            // Si es el título (idx === 0) y ya se dividió el texto
                            if (idx === 0 && data.titleSplit) {
                                // Animar palabras del título de abajo hacia arriba
                                gsap.to(data.titleSplit.words, {
                                    yPercent: 0,           // Posición final: sin desplazamiento
                                    duration: 0.6,
                                    ease: "power3.out",
                                    stagger: 0.05          // Retraso entre cada palabra
                                });
                            }
                            
                            // Si es un step (idx > 0)
                            if (idx > 0) {
                                const content = section.querySelector('.gallery-steps__step-content');
                                if (content) {
                                    // Animar contenido del step: fade in + slide up
                                    gsap.to(content, {
                                        opacity: 1,
                                        y: 0,
                                        duration: 0.6,
                                        ease: "power2.out"
                                    });
                                }
                            }
                        } else {
                            // SECCIÓN INACTIVA
                            section.classList.remove('active');
                            
                            // Si es el título, ocultar palabras hacia abajo
                            if (idx === 0 && data.titleSplit) {
                                gsap.to(data.titleSplit.words, {
                                    yPercent: 100,         // Desplazar 100% hacia abajo
                                    duration: 0.3,
                                    ease: "power2.in"
                                });
                            }
                            
                            // Si es un step, ocultar contenido
                            if (idx > 0) {
                                const content = section.querySelector('.gallery-steps__step-content');
                                if (content) {
                                    gsap.to(content, {
                                        opacity: 0,
                                        y: 30,
                                        duration: 0.3,
                                        ease: "power2.in"
                                    });
                                }
                            }
                        }
                    });
                    
                    // Actualizar navegación (los indicadores numerados)
                    if (data.navItems.length > 0) {
                        data.navItems.forEach((navItem, idx) => {
                            // Activar nav item si corresponde al step actual
                            // (localSection - 1 porque el nav no incluye el título)
                            navItem.classList.toggle('active', idx === localSection - 1);
                        });
                    }
                } else {
                    // CARD INACTIVA: ocultar todo
                    gsap.to(data.card, { opacity: 0, duration: 0.3 });
                    data.sections.forEach(section => section.classList.remove('active'));
                    data.navItems.forEach(nav => nav.classList.remove('active'));
                    
                    // Resetear animación de título
                    if (data.titleSplit) {
                        gsap.set(data.titleSplit.words, { yPercent: 100 });
                    }
                }
            });
        }

        // ========================================
        // FASE 6: OBSERVER (NAVEGACIÓN CON SCROLL)
        // ========================================
        
        // Crear Observer para capturar eventos de scroll, touch y pointer
        observer = Observer.create({
            target: container,                     // Elemento a observar
            type: "wheel,touch,pointer",           // Tipos de eventos a escuchar
            wheelSpeed: -1,                        // Invertir dirección del scroll
            
            // onDown: scroll hacia arriba (retroceder)
            onDown: () => {
                if (!isAnimating && currentSectionIndex > 0) {
                    // Si no estamos en la primera sección, ir a la anterior
                    goToSection(currentSectionIndex - 1);
                } else if (currentSectionIndex === 0) {
                    // Si estamos en la primera sección, permitir scroll normal hacia arriba
                    observer.disable();
                    window.scrollBy(0, -1);
                }
            },
            
            // onUp: scroll hacia abajo (avanzar)
            onUp: () => {
                if (!isAnimating && currentSectionIndex < totalSections - 1) {
                    // Si no estamos en la última sección, ir a la siguiente
                    goToSection(currentSectionIndex + 1);
                } else if (currentSectionIndex === totalSections - 1) {
                    // Si estamos en la última sección, permitir scroll normal hacia abajo
                    observer.disable();
                    window.scrollBy(0, 1);
                }
            },
            tolerance: 10,                         // Tolerancia al movimiento (evita triggers accidentales)
            preventDefault: true                   // Prevenir scroll por defecto
        });

        // Inicialmente el observer está desactivado (se activa cuando entramos al módulo)
        observer.disable();

        // ========================================
        // FASE 7: SPLIT TEXT (DIVISIÓN DE PALABRAS)
        // ========================================
        
        // Usar requestAnimationFrame doble para asegurar que el layout esté calculado
        // (importante para que SplitText detecte correctamente las líneas en móvil)
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                // Dividir texto de títulos en líneas y palabras
                cardData.forEach((data) => {
                    const title = data.sections[0];
                    if (title) {
                        const titleElement = title.querySelector('p, h2, h3, h4');
                        if (titleElement) {
                            // Dividir en líneas y palabras para la animación
                            data.titleSplit = new SplitText(titleElement, {
                                type: "lines,words",
                                linesClass: "line-split",
                                wordsClass: "word-split"
                            });
                            // Posición inicial: palabras desplazadas 100% hacia abajo
                            gsap.set(data.titleSplit.words, { yPercent: 100 });
                        }
                    }
                });
                
                // Inicializar primera sección después de que SplitText esté listo
                updateActiveStates(0);
            });
        });
    });

}

export default gallerySteps;